import { clsx } from 'clsx';

import { AssetResponse, BlockchainConfig, useGetAllAsset } from '@ping/api';
import { t } from '@ping/helpers';
import { Select } from '@ping/uikit/SelectA11Y';
import { DateTime } from '@ping/utils';

import style from './style.module.scss';

interface INetworkSelectProps extends ICustomizable {
  assetId?: string;
  selectedKey?: string;
  label?: string;
  isDisabled?: boolean;
  magnet?: IMagnet;
  onChange?(network: string, config: BlockchainConfig): void;
  /** @description it shows the item with "NOT AVAILABLE" badge and ignores its selection */
  ignore?(network: BlockchainConfig): boolean;
}

export function NetworkSelect(props: INetworkSelectProps) {
  const { data: assets, isLoading: isAssetsLoading } = useGetAllAsset({
    query: {
      staleTime: DateTime.days(1),
      select: ({ data: allAssets }) => {
        return allAssets.reduce((map, asset) => map.set(asset.id, asset), new Map<string, AssetResponse>());
      },
    },
  });
  const allAssetNetworks: BlockchainConfig[] = (assets?.get(props.assetId) as any)?.blockchain_configs || [];

  const handleOnSelectionChange = (network: string) => {
    const config = allAssetNetworks.find(item => item.blockchain_network === network);
    if (props.ignore?.(config)) {
      return;
    }

    props.onChange?.(network, config);
  };

  return (
    <Select
      className={clsx(style['network-select'], props.className)}
      items={allAssetNetworks}
      isLoading={isAssetsLoading}
      selectedKey={props.selectedKey}
      isDisabled={props.isDisabled}
      magnet={props.magnet}
      label={props.label}
      onSelectionChange={handleOnSelectionChange}
      emptyState={t('No networks')}
      buttonContent={props.selectedKey ?? t('Select a network')}
      topElement={
        <div>
          <p className={style['network-select__title']}>{t('Select network')}</p>
          <p className={style['network-select__subtitle']}>
            {t(
              'Confirm the network you choose to deposit matches the withdrawal network, otherwise assets will be lost'
            )}
          </p>
        </div>
      }
    >
      {network => {
        const mustBeIgnored = props.ignore?.(network);

        return (
          <Select.Item
            className={clsx(style['network-select__item'], {
              [style['network-select__item--disabled']]: mustBeIgnored,
            })}
            key={network.blockchain_network}
            textValue={network.blockchain_network}
            aria-label={network.blockchain_network}
          >
            <span className={style['network-select__name']}>{network.blockchain_network}</span>
            {mustBeIgnored && <span className={style['network-select__tag']}>{t('not available')}</span>}
          </Select.Item>
        );
      }}
    </Select>
  );
}
